import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { DASHBOARD_PREFIX, DEFAULT_DASHBOARD_ID } from '@/features/Dashboard/constants/const';
import { buildDashboardPayload, normalizeTabEntry } from '@/features/Dashboard/lib/transformers';
import {
  DashboardStructure,
  DashboardTab,
  MarketApps,
  NestedDashboardStructure
} from '@/features/Dashboard/types';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import usePreferences from '@/pages/Profile/Preferences/usePreferences';
import { useTripTokenFromStorage } from '@/services/storage';
import { type Option, saveUserConfig, UserConfig } from '@/slices/userConfigSlice';
import { AppDispatch } from '@/store';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import { dashboardQueryKeys } from './queries';

export const SAVE_SNACKBAR_KEY = 'dashboard-save';

const updateHomepage = (option: Option, dispatch: AppDispatch, userConfig: UserConfig) => {
  dispatch(
    saveUserConfig({
      userConfig: {
        ...userConfig,
        homepage: option
      }
    })
  );
};

export const useUpdateDashboard = (
  dashboardName = 'My Dashboard',
  successMessage = 'Dashboard updated!',
  tabName = 'Main',
  oldDashboardName?: string
) => {
  const { showErrorSnackbar, showSuccessSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { changeUserSetting, homepage } = usePreferences();
  const token = useTripTokenFromStorage();
  return useMutation({
    mutationKey: ['dashboard', dashboardName, oldDashboardName],
    mutationFn: async (data: MarketApps | DashboardStructure) => {
      showLoadingSnackbar('Saving dashboard...', SAVE_SNACKBAR_KEY);

      // Check if data is a full DashboardStructure (has dashboard name as key)
      // This happens when saving all tabs with their order (from edit mode)
      const isFullStructure =
        typeof data === 'object' &&
        !Array.isArray(data) &&
        dashboardName in data &&
        typeof (data as any)[dashboardName] === 'object' &&
        // Ensure it's not just a single tab wrapped in an object
        Object.keys((data as any)[dashboardName]).length > 0;

      let payloadForServer: DashboardTab;

      if (isFullStructure) {
        payloadForServer = (data as NestedDashboardStructure)[dashboardName];
      } else {
        // Single tab update - build payload and merge with existing tabs
        const newTabPayload: DashboardTab = buildDashboardPayload(data, dashboardName, tabName);

        // Preserve other tabs by merging with existing dashboard structure
        const cached = queryClient.getQueryData<Record<string, DashboardTab>>(
          dashboardQueryKeys.list(false, token)
        );
        const existingTabs = cached?.[oldDashboardName || dashboardName] || {};

        // Preserve tab order by starting with existing tabs, then updating only
        // the current tab. This ensures tab order doesn't change and existing tab
        // IDs are preserved
        const effectiveTabName = tabName || 'Main';
        const updatedTabs: DashboardTab = {};

        // First, copy all existing tabs in their original order
        Object.entries(existingTabs).forEach(([name, value]) => {
          updatedTabs[name] = normalizeTabEntry(name, value);
        });

        // Then, update only the current tab's content (preserving its ID if it exists)
        const newTabValue = newTabPayload[effectiveTabName];
        if (newTabValue) {
          const normalizedNewTab = normalizeTabEntry(effectiveTabName, newTabValue);
          const existingTab = existingTabs[effectiveTabName]
            ? normalizeTabEntry(effectiveTabName, existingTabs[effectiveTabName])
            : null;
          updatedTabs[effectiveTabName] = {
            id: existingTab?.id || normalizedNewTab.id,
            content: normalizedNewTab.content
          };
        } else {
          // If tab doesn't exist in new payload, create it with existing ID or new one
          const existingTab = existingTabs[effectiveTabName]
            ? normalizeTabEntry(effectiveTabName, existingTabs[effectiveTabName])
            : null;
          updatedTabs[effectiveTabName] = {
            id: existingTab?.id || generateUniqueUUID(new Set()),
            content: Array.isArray(data) ? data : []
          };
        }

        payloadForServer = updatedTabs;
      }
      let response;
      if (oldDashboardName !== dashboardName && oldDashboardName) {
        response = await httpInstances.axiosInternalInstance.patch(
          `/config/dashboard/${encodeURIComponent(oldDashboardName)}`,
          payloadForServer,
          {
            params: {
              newKey: dashboardName
            }
          }
        );
      } else {
        response = await httpInstances.axiosInternalInstance.post(
          `/config/dashboard/${encodeURIComponent(dashboardName)}`,
          payloadForServer
        );
      }

      await new Promise(resolve => {
        setTimeout(resolve, 2000);
      });

      return response;
    },
    onError: () => {
      showErrorSnackbar("Oops! We couldn't save your dashboard. Please try again.");
    },
    onSuccess: async () => {
      if (successMessage) {
        showSuccessSnackbar(successMessage, SAVE_SNACKBAR_KEY);
      }
      const cached = queryClient.getQueryData<Record<string, DashboardTab>>(
        dashboardQueryKeys.list(false, token)
      );

      if (cached?.[DEFAULT_DASHBOARD_ID]) {
        await httpInstances.axiosInternalInstance.delete(
          `/config/dashboard/${DEFAULT_DASHBOARD_ID}`
        );
        await new Promise(resolve => {
          setTimeout(resolve, 2000);
        });
      }

      // Refetch dashboard data to ensure it's up to date before navigation
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });

      if (oldDashboardName !== dashboardName) {
        // Preserve current tab when navigating to renamed dashboard
        const effectiveTabName = tabName || 'Main';
        navigate(
          `/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(effectiveTabName)}`
        );
        // If user's homepage points to this dashboard, update it to the new name
        if (
          homepage.value === DASHBOARD_PREFIX + oldDashboardName ||
          homepage.value === oldDashboardName
        ) {
          changeUserSetting(
            'homepage',
            {
              label: dashboardName,
              value: DASHBOARD_PREFIX + dashboardName
            },
            false
          );
          return;
        }
      }
      if (dashboardName === 'My Dashboard' && homepage.value === 'my-dashboard') {
        changeUserSetting(
          'homepage',
          {
            label: dashboardName,
            value: DASHBOARD_PREFIX + dashboardName
          },
          false
        );
      }
    }
  });
};

export const useDeleteDashboard = (dashboardName: string) => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userConfig = useAppSelector(state => state.userConfig.userConfig);
  const homepage = userConfig.homepage;
  const token = useTripTokenFromStorage();
  return useMutation({
    mutationKey: ['dashboard', 'delete', dashboardName],
    mutationFn: async () => {
      const response = await httpInstances.axiosInternalInstance.delete(
        `/config/dashboard/${encodeURIComponent(dashboardName)}`
      );
      await new Promise(resolve => {
        setTimeout(resolve, 2000);
      });
      return response;
    },
    onError: () => {
      showErrorSnackbar("Oops! We couldn't delete your dashboard. Please try again.");
    },
    onSuccess: () => {
      showSuccessSnackbar('Dashboard deleted!');

      // Manually remove the deleted dashboard from cache immediately
      const cached = queryClient.getQueryData<Record<string, DashboardTab>>(
        dashboardQueryKeys.list(false, token)
      );
      let cacheAfterDelete = cached;
      if (cached && dashboardName in cached) {
        const updatedCache = { ...cached };
        delete updatedCache[dashboardName];
        queryClient.setQueryData(dashboardQueryKeys.list(false, token), updatedCache);
        cacheAfterDelete = updatedCache;
      }

      // Refetch to get fresh data from server
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });

      // Navigate to the first available dashboard or to /dashboard/new
      const remainingDashboards = cacheAfterDelete
        ? Object.keys(cacheAfterDelete).filter(name => name !== dashboardName)
        : [];

      if (remainingDashboards.length > 0) {
        const firstDashboard = remainingDashboards[0];
        const firstDashboardTabs = cacheAfterDelete?.[firstDashboard];
        if (firstDashboardTabs) {
          const firstTabName = Object.keys(firstDashboardTabs)[0];
          navigate(
            `/dashboard/${encodeURIComponent(firstDashboard)}/${encodeURIComponent(firstTabName)}`
          );
        }
        if (homepage.value === DASHBOARD_PREFIX + dashboardName) {
          updateHomepage(
            {
              label: firstDashboard,
              value: DASHBOARD_PREFIX + firstDashboard
            },
            dispatch,
            userConfig
          );
        }
      } else {
        navigate('/dashboard/new');
        if (homepage.value === DASHBOARD_PREFIX + dashboardName) {
          updateHomepage({ label: 'My Dashboard', value: `my-dashboard` }, dispatch, userConfig);
        }
      }
    }
  });
};
